import { Link } from "react-router-dom";
import { Star, ShoppingBag, Gift, Bookmark, Plus, Wine } from "lucide-react";
import { toast } from "sonner";

export function Logo({ className = "h-8 w-8" }) {
  return (
    <svg viewBox="0 0 48 48" className={className} fill="none" aria-hidden>
      <path d="M12 6h24l-3 8c-1 3-3 5-6 6v12h5v4H16v-4h5V20c-3-1-5-3-6-6l-3-8z" fill="hsl(var(--primary))" />
      <path d="M16 10h16l-1.5 4h-13L16 10z" fill="hsl(var(--secondary))" opacity="0.9" />
    </svg>
  );
}

export function Wordmark() {
  return (
    <div className="flex items-center gap-2">
      <Logo />
      <span className="font-display text-2xl leading-none tracking-tight">
        Stock<span className="text-primary">My</span>Bar
      </span>
    </div>
  );
}

export function StarRating({ value, size = 14, showNum = true }) {
  return (
    <span className="inline-flex items-center gap-1 text-primary" title={value}>
      <span className="inline-flex">
        {[1, 2, 3, 4, 5].map((i) => (
          <Star key={i} size={size} className={i <= Math.round(value) ? "fill-primary" : "fill-none opacity-40"} strokeWidth={1.5} />
        ))}
      </span>
      {showNum && <span className="text-xs font-semibold text-foreground/80">{value}</span>}
    </span>
  );
}

export function Chip({ active, children, ...props }) {
  return (
    <button
      className={`rounded-full border px-4 py-1.5 text-sm font-medium transition-colors ${
        active ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:border-primary/40 hover:text-foreground"
      }`}
      {...props}
    >
      {children}
    </button>
  );
}

export function Badge({ children }) {
  return <span className="inline-flex items-center gap-1 rounded-full bg-secondary/10 px-2.5 py-0.5 text-xs font-medium text-secondary dark:text-secondary-foreground/90">{children}</span>;
}

export function PriceBand({ band }) {
  return <span className="text-sm font-semibold text-muted-foreground">{band}</span>;
}

export function PartnerButtons({ partners = [], label = "Shop This Bottle", compact }) {
  const act = (p) => toast.success(`Opening ${p.partner}`, { description: `${p.availability} · $${p.price}. Availability varies by partner & local regulations.` });
  return (
    <div className="space-y-2">
      <button data-testid="shop-primary-btn" onClick={() => act(partners[0] || { partner: "partner", availability: "", price: "" })}
        className="flex w-full items-center justify-center gap-2 rounded-full bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground transition-transform hover:scale-[1.02]">
        <ShoppingBag size={16} /> {label}
      </button>
      {!compact && partners.length > 1 && (
        <div className="grid grid-cols-2 gap-2">
          {partners.slice(0, 4).map((p) => (
            <button key={p.partner} data-testid={`partner-${p.partner}`} onClick={() => act(p)}
              className="flex flex-col items-start rounded-lg border border-border px-3 py-2 text-left text-xs transition-colors hover:border-primary/50">
              <span className="font-semibold">{p.partner}</span>
              <span className="text-muted-foreground">${p.price} · {p.availability}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function ProductCard({ p }) {
  return (
    <div data-testid={`product-card-${p.id}`} className="card-lift group overflow-hidden rounded-xl border border-border bg-card">
      <Link to={`/ratings/${p.id}`} className="block">
        <div className="relative aspect-[4/5] overflow-hidden bg-muted">
          <img src={p.image} alt={p.name} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
          <span className="absolute left-3 top-3 rounded-full glass px-2.5 py-1 text-xs font-medium">{p.category}</span>
        </div>
      </Link>
      <div className="space-y-2 p-4">
        <div className="flex items-start justify-between gap-2">
          <Link to={`/ratings/${p.id}`}><h3 className="font-semibold leading-tight hover-underline">{p.name}</h3></Link>
          <PriceBand band={p.price_band} />
        </div>
        <div className="flex items-center justify-between">
          <StarRating value={p.rating} />
          <span className="text-xs text-muted-foreground">{p.reviews?.toLocaleString()} reviews</span>
        </div>
        <div className="flex flex-wrap gap-1.5 pt-1">
          {(p.tags || []).slice(0, 3).map((t) => <span key={t} className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">{t}</span>)}
        </div>
        <PartnerButtons partners={p.partners} compact />
      </div>
    </div>
  );
}

export function RecipeCard({ r, onSave }) {
  return (
    <div data-testid={`recipe-card-${r.id}`} className="card-lift group overflow-hidden rounded-xl border border-border bg-card">
      <Link to={`/recipes/${r.id}`} className="block">
        <div className="relative overflow-hidden bg-muted" style={{ aspectRatio: "3/4" }}>
          <img src={r.image} alt={r.name} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
          <span className="absolute right-3 top-3 rounded-full glass px-2 py-1 text-xs font-medium">{r.difficulty}</span>
        </div>
      </Link>
      <div className="space-y-2 p-4">
        <div className="flex items-center justify-between">
          <span className="overline text-primary">{r.spirit}</span>
          <StarRating value={r.rating} size={12} />
        </div>
        <Link to={`/recipes/${r.id}`}><h3 className="font-display text-xl leading-tight hover-underline">{r.name}</h3></Link>
        <p className="text-sm text-muted-foreground">{r.profile}</p>
        <div className="flex items-center justify-between pt-1 text-xs text-muted-foreground">
          <span>⏱ {r.prep}</span>
          <div className="flex gap-2">
            <button data-testid={`recipe-save-${r.id}`} onClick={() => onSave?.(r)} className="inline-flex items-center gap-1 hover:text-primary"><Bookmark size={14} /> Save</button>
            <button onClick={() => toast.success("Added to shopping list")} className="inline-flex items-center gap-1 hover:text-primary"><Plus size={14} /> List</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function StatCard({ icon: Icon, label, value, hint }) {
  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <div className="flex items-center gap-2 text-muted-foreground"><Icon size={16} /> <span className="text-sm">{label}</span></div>
      <div className="mt-2 font-display text-3xl">{value}</div>
      {hint && <div className="text-xs text-muted-foreground">{hint}</div>}
    </div>
  );
}

export function SectionHeader({ overline, title, action }) {
  return (
    <div className="mb-6 flex items-end justify-between gap-4">
      <div>
        {overline && <div className="overline mb-2 text-primary">{overline}</div>}
        <h2 className="font-display text-3xl sm:text-4xl tracking-tight">{title}</h2>
      </div>
      {action}
    </div>
  );
}

export function Skeleton({ className = "" }) {
  return <div className={`animate-pulse rounded-lg bg-muted ${className}`} />;
}

export function EmptyState({ icon: Icon = Wine, title, hint }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-border py-16 text-center">
      <Icon className="mb-3 text-muted-foreground" />
      <p className="font-semibold">{title}</p>
      {hint && <p className="mt-1 text-sm text-muted-foreground">{hint}</p>}
    </div>
  );
}

export function ComplianceNote({ children }) {
  return <p className="text-xs text-muted-foreground">{children || "Must be 21+ to purchase. Availability varies by partner and local regulations. Delivery or local pickup handled by our licensed partners."}</p>;
}
